import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../../hooks/AuthProvider";
import Filter from "../Filter";
import AudienceTableComponent from "../Audience/AudienceTableComponent";
import AudienceFilterChip from "../Audience/AudienceFilterChip";

import {
  AUDIENCE_SEGMENT_UPDATE_ENDPOINT,
  AUDIENCE_SEGMENT_DETAILS_ENDPOINT,
  AUDIENCE_GET_ENDPOINT,
  TAG_LIST_ENDPOINT,
} from "../../config/urls";
import { ArrowBack, ArrowLeft } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import BackButton from "../../common/back-button";

const DEFAULT_COLUMNS = [
  { id: "name", label: "Name" },
  { id: "phone_no", label: "Phone" },
  { id: "email", label: "Email" },
  { id: "language", label: "Language" },
  { id: "gender", label: "Gender" },
  { id: "dob", label: "Date of Birth" },
  { id: "city", label: "City" },
  { id: "state", label: "State" },
  { id: "tags", label: "Tags" },
  { id: "opted_in", label: "Opted In" },
  { id: "updated_by", label: "Updated By" },
  { id: "updated_at", label: "Last Updated" },
];

const UpdateSegment = ({ onNavigate }) => {
  const storedParams = localStorage.getItem("activePageParams");
  const params = storedParams ? JSON.parse(storedParams) : {};
  const segmentId = params.segmentId;

  const { getValidToken } = useAuth();

  const [segmentName, setSegmentName] = useState("");
  const [filters, setFilters] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [allTagOptions, setAllTagOptions] = useState([]);

  const filterFields = [
    { label: "Name", type: "text" },
    { label: "Phone", type: "phone" },
    { label: "Email", type: "text" },
    { label: "Language", type: "text" },
    { label: "City", type: "text" },
    { label: "State", type: "text" },
    {
      label: "Tags",
      type: "list",
      values: allTagOptions.map((tag) => tag.name),
    },
    { label: "Marketing Opt In", type: "boolean", display: ["Yes", "No"] },
    { label: "Gender", type: "single_select_list", values: ["Male", "Female", "N/A"] },
    { label: "DOB", type: "date" },
    { label: "Created At", type: "date" },
    { label: "Last Updated", type: "date" },
  ];

  // Fetch audiences based on filters
  const fetchAudiences = async (appliedFilters, pageNo, pageSize) => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams({
        limit: pageSize,
        offset: pageNo * pageSize,
      });

      appliedFilters.forEach(({ field, operator, value }) => {
        const f = field.toLowerCase().replaceAll(" ", "_");
        const o = operator.toLowerCase().replaceAll(" ", "_");
        queryParams.append(f, `${o}:${value ?? ""}`);
      });

      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenant,
      };

      const res = await fetch(`${AUDIENCE_GET_ENDPOINT}?${queryParams}`, {
        headers,
      });

      if (!res.ok) throw new Error("Failed to fetch audiences");
      const data = await res.json();

      setAudiences(data.results || []);
      setTotalCount(data.count || 0);
    } catch (err) {
      console.error(err);
      setAudiences([]);
      setErrorMsg("Failed to fetch audiences.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSegment = async () => {
    try {
      setLoading(true);
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenant,
      };

      const res = await fetch(
        `${AUDIENCE_SEGMENT_DETAILS_ENDPOINT}${segmentId}`,
        { headers },
      );

      if (!res.ok) throw new Error("Failed to fetch segment");

      const data = await res.json();
      setSegmentName(data.name);
      setFilters(data.filters || []);
      fetchAudiences(data.filters || [], 0, rowsPerPage);
    } catch (err) {
      console.error(err);
      setErrorMsg("Unable to load segment details.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTagOptions = async () => {
    const url = TAG_LIST_ENDPOINT;
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      setAllTagOptions(data);
    } catch (error) {
      console.error("Error fetching tag options:", error);
    }
  };

  // Update segment
  const handleUpdateSegment = async () => {
    if (!segmentName.trim()) {
      setErrorMsg("Segment name cannot be empty.");
      return;
    }
    if (filters.length === 0) {
      setErrorMsg("At least one filter is required.");
      return;
    }

    try {
      setErrorMsg("");
      setSuccessMsg("");

      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(
        `${AUDIENCE_SEGMENT_UPDATE_ENDPOINT}${segmentId}/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
          body: JSON.stringify({ name: segmentName, filters }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();

        if (typeof errorData === "object") {
          let _errorMsg = "";

          for (const key in errorData) {
            _errorMsg += errorData[key] + "\n";
          }

          setErrorMsg(_errorMsg);
        } else {
          setErrorMsg(errorData || "Failed to save segment.");
        }
        return;
      }

      setSuccessMsg("Segment updated successfully!");
      fetchAudiences(filters, page, rowsPerPage);

      // Redirect to segment list after 1.5 seconds
      setTimeout(() => {
        setSuccessMsg("");
        onNavigate("audience", { tab: 1 });
      }, 1500);
    } catch (err) {
      console.error(err);
      setErrorMsg("Unable to update segment. Please try again.");
    }
  };

  // Fetch segment details on segment ID change
  useEffect(() => {
    fetchSegment();
  }, [segmentId]);

  // Fetch tag options on mount
  useEffect(() => {
    fetchTagOptions();
  }, []);

  return (
    <Box sx={{ px: 3 }}>
      <BackButton
        onClick={() => {
          onNavigate("audience", { tab: 1 });
        }}
      />

      <h2 className="heading-title">Update Segment</h2>

      <Stack direction="column" spacing={2}>
        {/* Header */}
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "flex-start",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Filter
              fields={filterFields}
              onApply={(newFilters) => {
                setFilters(newFilters);
                setPage(0);
                fetchAudiences(newFilters, 0, rowsPerPage);
              }}
              initalFilters={filters}
            />
            {/* Applied Filters */}
            {filters.length > 0 && (
              <Stack direction="row" gap={1} flexWrap="wrap">
                {filters.map((f, i) => (
                  <AudienceFilterChip key={i} filter={f} />
                ))}
              </Stack>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <TextField
              label="Segment Name"
              placeholder="Enter a name for your segment"
              variant="outlined"
              size="small"
              value={segmentName}
              onChange={(e) => setSegmentName(e.target.value)}
              sx={{ flex: 1 }}
            />
            <ButtonOne
              variant="contained"
              onClick={handleUpdateSegment}
              disabled={loading}
            >
              Update Segment
            </ButtonOne>
          </Box>
        </Stack>

        {/* Alerts */}
        {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
        {successMsg && <Alert severity="success">{successMsg}</Alert>}

        {/* Audience Table */}
        <AudienceTableComponent
          columns={DEFAULT_COLUMNS}
          audienceData={audiences}
          rowCount={totalCount}
          controller={{ page, rowsPerPage }}
          tenant={localStorage.getItem("tenant")}
          tenantSpecificColumns={{}}
          handleSelectAll={() => {}}
          handleSelectRecord={() => {}}
          isRecordSelected={() => false}
          setSelectedEditAudience={() => {}}
          setEditAudienceModalOpen={() => {}}
          handleDeleteAudienceClick={() => {}}
          handlePageChange={(e, newPage) => {
            setPage(newPage);
            fetchAudiences(filters, newPage, rowsPerPage);
          }}
          handleChangeRowsPerPage={(e) => {
            const newSize = parseInt(e.target.value, 10);
            setRowsPerPage(newSize);
            setPage(0);
            fetchAudiences(filters, 0, newSize);
          }}
          reformatDate={(date) =>
            date ? new Date(date).toLocaleDateString() : "—"
          }
        />
      </Stack>
    </Box>
  );
};

export default UpdateSegment;
